import { REPO_CONNECTOR_TYPES } from '@/components/projects/workflows/shared/repoConnectorTypes'
import type { TriggerInputField } from '@/types/workflowDefinition'
import type { Connector, ConnectorType } from '@/types'
import type { RepoPromptOverride } from '@/types/claude'

export const emptySelection = (connectorType: string, connectorId?: string): RepoPromptOverride => ({
  connectorType,
  connectorId,
  repository: '',
  branch: undefined,
  filePath: '',
})

export function resolveDefaultConnector(
  field: TriggerInputField,
  connections: readonly Connector[] = [],
): { connectorType: string; connectorId?: string } {
  const configured = (field.connectorType ? [field.connectorType] : REPO_CONNECTOR_TYPES) as ConnectorType[]
  const connectorType = configured.find(t => REPO_CONNECTOR_TYPES.includes(t)) ?? ''
  const connectorId = connections.find(c => c.type === connectorType && c.isEnabled !== false)?.id
  return { connectorType, connectorId }
}

export function parseSelections(value: string): RepoPromptOverride[] {
  if (!value) return []
  const trimmed = value.trim()
  try {
    if (trimmed.startsWith('[')) return JSON.parse(trimmed) as RepoPromptOverride[]
    return [JSON.parse(trimmed) as RepoPromptOverride]
  } catch {
    return []
  }
}

export function normalizeFolder(folder?: string | null): string {
  if (!folder) return ''
  let start = 0
  let end = folder.length
  while (start < end && folder[start] === '/') start++
  while (end > start && folder[end - 1] === '/') end--
  return folder.slice(start, end)
}

export function parseAccept(accept?: string | null): string[] {
  return accept?.split(',').map(e => e.trim().toLowerCase()).filter(Boolean).map(e => (e.startsWith('.') ? e : `.${e}`)) ?? []
}

export function matchesAccept(filePath: string, acceptExtensions: string[]): boolean {
  if (acceptExtensions.length === 0) return true
  const lower = filePath.toLowerCase()
  const dot = lower.lastIndexOf('.')
  const ext = dot >= 0 ? lower.slice(dot) : ''
  return acceptExtensions.includes(ext)
}

export function selectionError(selection: RepoPromptOverride, folder: string, acceptExtensions: string[]): string | null {
  const filePath = selection.filePath?.trim().replace(/^\/+/, '')
  if (!filePath) return null
  if (!matchesAccept(filePath, acceptExtensions)) {
    return `Only files ending in ${acceptExtensions.join(', ')} are allowed.`
  }
  if (folder && !filePath.toLowerCase().startsWith(`${folder.toLowerCase()}/`)) {
    return `File must be inside the "${folder}/" folder.`
  }
  return null
}

export function validateRepoFilePickerValue(value: string, field: TriggerInputField): string | true {
  if (!value) return true
  const selections = parseSelections(value)
  if (selections.length === 0) return true
  const folder = normalizeFolder(field.folder)
  const acceptExtensions = parseAccept(field.accept)
  for (const selection of selections) {
    if (!selection.connectorType || !selection.repository || !selection.filePath) {
      return 'Each selected document needs a connector, repository, and file path.'
    }
    const err = selectionError(selection, folder, acceptExtensions)
    if (err) return err
  }
  return true
}
